import {memo, useLayoutEffect, useRef} from 'react';
import {translate} from '../../utils';
import {useAddGuestsStatic} from './AddGuestsContext';
import {COLUMN_HEADERS_TRANSLATION_KEYS} from './addGuestsUtilities';
import {AddGuestsTableRoomRows} from './AddGuestsTableRoomRows';

const COLUMN_HINTS_TRANSLATION_KEYS = {
    language: 'CLIENT_LANGUAGE_TOOLTIP_TEXT',
    stayPreferences: 'STAY_PREFERENCES_TOOLTIP_TEXT',
    mealPreferences: 'MEAL_PREFERENCES_TOOLTIP_TEXT',
    comments: 'CLIENT_COMMENTS_TOOLTIP_TEXT',
};

export const AddGuestsTable = memo(function AddGuestsTable({
    errors,
    rooms,
    setErrors,
    setFieldValue,
    regionMaps,
    onRegionMapsUpdate,
    onAddGuest,
    onRemoveGuest,
    onSelectClientSuggestion,
}) {
    const {guestColumnKeys} = useAddGuestsStatic();

    const tableScrollContainerRef = useRef(null);

    const guestsCount = rooms.reduce((total, room) => total + room.guests.length, 0);

    useLayoutEffect(() => {
        const tableScrollContainer = tableScrollContainerRef.current;

        const leftSideStickyCellSelectors = [
            'thead th[data-column-key="roomKeyAndCategory"]',
            'tbody td[data-column-key="roomKeyAndCategory"]',
            'tbody td[data-column-key="addGuest"]',
        ].join(', ');

        const rightSideStickyCellSelectors = [
            'thead th[data-column-key="guestActions"]',
            'tbody td[data-column-key="guestActions"]',
        ].join(', ');

        const updateSticky = () => {
            const scrollLeft = tableScrollContainer.scrollLeft;
            const maxScrollLeft = tableScrollContainer.scrollWidth - tableScrollContainer.clientWidth;
            const isStuckOnLeft = scrollLeft > 0;
            const isStuckOnRight = scrollLeft < maxScrollLeft;

            tableScrollContainer.querySelectorAll(leftSideStickyCellSelectors).forEach((cell) => {
                if (isStuckOnLeft) {
                    cell.setAttribute('data-stuck', '');
                } else {
                    cell.removeAttribute('data-stuck');
                }
            });

            tableScrollContainer.querySelectorAll(rightSideStickyCellSelectors).forEach((cell) => {
                if (isStuckOnRight) {
                    cell.setAttribute('data-stuck', '');
                } else {
                    cell.removeAttribute('data-stuck');
                }
            });
        };

        updateSticky();
        tableScrollContainer.addEventListener('scroll', updateSticky);

        return () => tableScrollContainer.removeEventListener('scroll', updateSticky);
    }, [guestsCount]); // Handles case when guests count in a room goes from 2 to 1

    return (
        <div ref={tableScrollContainerRef} className="table-responsive">
            <table id="add-guests-table" className="table">
                <thead>
                    <tr>
                        <th data-column-key="roomKeyAndCategory">
                            {`${translate('frontend', COLUMN_HEADERS_TRANSLATION_KEYS.roomKey)} | ${translate('frontend', COLUMN_HEADERS_TRANSLATION_KEYS.category)}`}
                        </th>

                        {guestColumnKeys.map(guestColumnKey => (
                            <th key={guestColumnKey} data-column-key={guestColumnKey}>
                                {translate('frontend', COLUMN_HEADERS_TRANSLATION_KEYS[guestColumnKey])}

                                {COLUMN_HINTS_TRANSLATION_KEYS[guestColumnKey] ? (
                                    <i
                                        className="fa fa-info-circle more-info ml-1"
                                        data-toggle="popover"
                                        data-container="body"
                                        data-content={translate('frontend', COLUMN_HINTS_TRANSLATION_KEYS[guestColumnKey])}
                                        title=""
                                    />
                                ) : null}
                            </th>
                        ))}

                        <th data-column-key="guestActions" />
                    </tr>
                </thead>

                <tbody>
                    {rooms.map((room, roomIndex) => (
                        <AddGuestsTableRoomRows
                            key={roomIndex}
                            room={room}
                            roomIndex={roomIndex}
                            isLastRoom={roomIndex === rooms.length - 1}
                            errors={errors}
                            setErrors={setErrors}
                            regionMaps={regionMaps}
                            onRegionMapsUpdate={onRegionMapsUpdate}
                            setFieldValue={setFieldValue}
                            onAddGuest={onAddGuest}
                            onRemoveGuest={onRemoveGuest}
                            onSelectClientSuggestion={onSelectClientSuggestion}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
});
